/**
 * @file app/list/page-client.tsx
 * @description 경매 리스트 페이지 - Client Component
 *
 * 클라이언트 상호작용(필터링, 정렬, 페이지네이션)을 처리합니다.
 * 초기 데이터는 Server Component에서 props로 받습니다.
 */

'use client';

import { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { Search } from 'lucide-react';
import { FilterBar } from '@/components/auction/list/FilterBar';
import { AuctionList } from '@/components/auction/list/AuctionList';
import { AuctionFilters, AuctionFilterUtils, AUCTION_FILTER_OPTIONS, RealAuction } from '@/lib/core/types';
import { getAuctionsListAction, getAuctionsListActionFast, getAuctionsCountAction } from '@/app/action/auction/search-auction';
import { cn } from '@/lib/utils/shared/core-utils';

// 실제 DB에서 경매 데이터를 가져오는 인터페이스
interface AuctionsListResponse {
  items: RealAuction[];
  total: number | null; // null일 수 있음 (초기 로드 시 카운트 제외)
  limit: number;
  offset: number;
}

/**
 * 캐시 키 생성 함수
 * 필터와 페이지 조합을 고유한 문자열로 변환
 */
function getCacheKey(filters: AuctionFilters, page: number): string {
  const regionsKey = filters.regions.sort().join(',');
  const typesKey = filters.propertyTypes.sort().join(',');
  return `${regionsKey}|${typesKey}|${filters.sortOrder}|${page}`;
}

/**
 * 전역 캐시 (모듈 레벨)
 * 같은 필터/페이지 조합에 대한 데이터를 메모리에 저장
 */
const auctionsCache = new Map<string, AuctionsListResponse>();

/**
 * 필터 상태를 사용하는 AuctionList 래퍼 컴포넌트
 */
function AuctionListWithFilters({
  auctionsData,
  filters,
  totalCount,
  onSortChange,
  onFiltersChange
}: {
  auctionsData: AuctionsListResponse | null;
  filters: AuctionFilters;
  totalCount: number;
  onSortChange: (sortOrder: typeof filters.sortOrder) => void;
  onFiltersChange: (filters: AuctionFilters) => void;
}) {
  const auctions = auctionsData?.items || [];
  const sortOrder = filters.sortOrder;

  return (
    <div className="space-y-6">
      {/* 필터바를 맨 위로 배치 */}
      <FilterBar
        showSortOptions={false}
        totalCount={totalCount}
        onFiltersChange={onFiltersChange}
      />

      <div className="px-1">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          검색 결과에서 사건을 열면 상세 검토 페이지로 바로 이동합니다.
        </p>
      </div>

      {/* 경매 물건 헤더 + 정렬 옵션 같은 라인 */}
      <div className="flex justify-between items-end px-1">
        <h2 className="text-xl font-bold text-zinc-800 dark:text-zinc-200">
          검토 가능한 사건 <span className="text-zinc-400 dark:text-zinc-500 font-medium text-sm ml-1">
            {auctionsData?.total === null && auctionsData?.items.length 
              ? `총 ${totalCount.toLocaleString()}+건` 
              : `총 ${totalCount.toLocaleString()}건`}
          </span>
        </h2>
        <div className="flex items-center gap-2">
          {AUCTION_FILTER_OPTIONS.sortOrders.map((sort) => (
            <button
              key={sort}
              onClick={() => onSortChange(sort)}
              className={cn(
                'px-3 py-2 text-sm font-bold transition whitespace-nowrap',
                sortOrder === sort
                  ? 'border-b-2 border-black dark:border-zinc-100 text-black dark:text-zinc-100'
                  : 'text-zinc-500 dark:text-zinc-400 hover:text-black dark:hover:text-zinc-100'
              )}
            >
              {sort}
            </button>
          ))}
        </div>
      </div>

      {/* 경매 리스트 */}
      <AuctionList auctions={auctions} filters={filters} />
    </div>
  );
}

interface AuctionListClientProps {
  initialData: AuctionsListResponse | null;
  initialError: string | null;
  initialFilters: AuctionFilters;
  initialPage: number;
  shouldLoadOnMount?: boolean;
}

/**
 * 경매 리스트 페이지의 Client Component
 * 초기 마운트 시 즉시 데이터를 로드하여 스켈레톤 UI를 먼저 렌더링합니다.
 */
export function AuctionListClient({
  initialData,
  initialError,
  initialFilters,
  initialPage,
  shouldLoadOnMount = false,
}: AuctionListClientProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [auctionsData, setAuctionsData] = useState<AuctionsListResponse | null>(initialData);
  const [loading, setLoading] = useState(shouldLoadOnMount && !initialData);
  const [error, setError] = useState<string | null>(initialError);
  const [filtersKey, setFiltersKey] = useState(0);
  const loadingRef = useRef(false); // 중복 요청 방지

  // 현재 페이지 계산
  const pageParam = searchParams.get('page');
  const currentPage = Math.max(1, parseInt(pageParam || '1', 10));

  // total이 null이면 items.length를 기반으로 임시 카운트 사용 (카운트 로딩 중)
  // items가 10개면 다음 페이지가 있을 가능성이 높으므로 최소한의 페이지네이션 표시
  const hasMorePages = auctionsData?.items.length === 10;
  const totalCount = auctionsData?.total ?? (auctionsData?.items.length ? auctionsData.items.length : 0);
  const totalPages = auctionsData?.total 
    ? Math.ceil(auctionsData.total / 10) 
    : (hasMorePages ? currentPage + 1 : currentPage); // total이 없으면 최소한 현재 페이지 + 1

  // 필터 상태 (메모이제이션으로 안정성 확보)
  const regionsParam = searchParams.get('regions');
  const typesParam = searchParams.get('types');
  const sortParam = searchParams.get('sort');

  const regions = useMemo(() =>
    AuctionFilterUtils.paramToRegions(regionsParam),
    [regionsParam]
  );
  const propertyTypes = useMemo(() =>
    AuctionFilterUtils.paramToTypes(typesParam),
    [typesParam]
  );
  const sortOrder = useMemo(() =>
    AuctionFilterUtils.paramToSort(sortParam),
    [sortParam]
  );

  // 필터나 페이지가 변경될 때만 데이터 리로드 (캐시 우선 확인)
  useEffect(() => {
    const currentFilters: AuctionFilters = { regions, propertyTypes, sortOrder };
    const cacheKey = getCacheKey(currentFilters, currentPage);
    
    // 캐시 확인
    const cachedData = auctionsCache.get(cacheKey);
    if (cachedData) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[AuctionListClient] 캐시에서 데이터 사용:', cacheKey);
      }
      setAuctionsData(cachedData);
      setLoading(false);
      return;
    }

    // 초기 데이터와 비교 (초기 로드 시 중복 요청 방지)
    const currentRegionsParam = regionsParam || '';
    const currentTypesParam = typesParam || '';
    const currentSortParam = sortParam || '';
    const initialRegionsParam = AuctionFilterUtils.regionsToParam(initialFilters.regions);
    const initialTypesParam = AuctionFilterUtils.typesToParam(initialFilters.propertyTypes);
    const initialSortParam = AuctionFilterUtils.sortToParam(initialFilters.sortOrder);
    
    const hasFilterChanged = 
      currentRegionsParam !== initialRegionsParam ||
      currentTypesParam !== initialTypesParam ||
      currentSortParam !== initialSortParam ||
      currentPage !== initialPage;

    if (!hasFilterChanged && initialData) {
      // 초기 데이터 사용 및 캐시 저장
      auctionsCache.set(cacheKey, initialData);
      return;
    }

    // 이미 로딩 중이면 중복 요청 방지
    if (loadingRef.current) {
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[AuctionListClient] 필터/페이지 변경 감지, 데이터 리로딩');
    }

    const loadAuctions = async () => {
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const result = await getAuctionsListAction({
          filters: {
            regions: regions.length > 0 ? regions : undefined,
            propertyTypes: propertyTypes.length > 0 ? propertyTypes : undefined,
            sortOrder: sortOrder,
          },
          limit: 10,
          offset: (currentPage - 1) * 10,
        });

        if (result.error) {
          setError(result.error);
        } else if (result.data) {
          // 캐시에 저장
          auctionsCache.set(cacheKey, result.data);
          setAuctionsData(result.data);
        }
      } catch {
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadAuctions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionsParam, typesParam, sortParam, currentPage, filtersKey, initialFilters, initialPage, regions, propertyTypes, sortOrder]);

  // 초기 마운트 시 즉시 데이터 로드 (하이브리드 접근)
  useEffect(() => {
    if (shouldLoadOnMount && !auctionsData && !loading && !loadingRef.current) {
      const cacheKey = getCacheKey(initialFilters, initialPage);
      const cachedData = auctionsCache.get(cacheKey);
      
      if (cachedData) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[AuctionListClient] 초기 로드: 캐시에서 데이터 사용');
        }
        setAuctionsData(cachedData);
        return;
      }

      const loadInitialData = async () => {
        loadingRef.current = true;
        setLoading(true);
        setError(null);

        try {
          // 초기 로드 시에는 경량 액션 사용 (카운트 제외, 디버깅 쿼리 제외)
          const result = await getAuctionsListActionFast({
            filters: {
              regions: initialFilters.regions.length > 0 ? initialFilters.regions : undefined,
              propertyTypes: initialFilters.propertyTypes.length > 0 ? initialFilters.propertyTypes : undefined,
              sortOrder: initialFilters.sortOrder,
            },
            limit: 10,
            offset: (initialPage - 1) * 10,
          });

          if (result.error) {
            setError(result.error);
          } else if (result.data) {
            // 캐시에 저장
            auctionsCache.set(cacheKey, result.data);
            setAuctionsData(result.data);
            
            // 카운트는 별도로 비동기 로드 (UI 블로킹 없음)
            if (result.data.total === null) {
              getAuctionsCountAction({
                regions: initialFilters.regions.length > 0 ? initialFilters.regions : undefined,
                propertyTypes: initialFilters.propertyTypes.length > 0 ? initialFilters.propertyTypes : undefined,
              }).then((countResult) => {
                if (countResult.data) {
                  setAuctionsData(prev => {
                    if (!prev) return null;
                    const updatedData = {
                      ...prev,
                      total: countResult.data.count
                    };
                    // 캐시도 업데이트
                    auctionsCache.set(cacheKey, updatedData);
                    return updatedData;
                  });
                } else if (countResult.error && process.env.NODE_ENV === 'development') {
                  console.warn('[AuctionListClient] 카운트 조회 실패:', countResult.error);
                }
              }).catch((err) => {
                // 카운트 조회 실패해도 무시 (데이터는 이미 표시됨)
                if (process.env.NODE_ENV === 'development') {
                  console.warn('[AuctionListClient] 카운트 조회 중 에러:', err);
                }
              });
            }
          }
        } catch {
          setError('데이터를 불러오는 중 오류가 발생했습니다.');
        } finally {
          setLoading(false);
          loadingRef.current = false;
        }
      };

      loadInitialData();
    }
  }, [shouldLoadOnMount, initialFilters, initialPage, auctionsData, loading]);

  // 페이지 변경 핸들러
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (newPage === 1) {
      params.delete('page');
    } else {
      params.set('page', newPage.toString());
    }
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  // 정렬 변경 핸들러
  const handleSortChange = (newSortOrder: typeof sortOrder) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('sort', AuctionFilterUtils.sortToParam(newSortOrder));
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Main Content */}
      <main className="flex-1 max-w-screen-xl mx-auto px-4 md:px-6 py-8 w-full">
        <section className="mb-8 md:mb-10">
          <div className="max-w-3xl">
            <p className="text-[11px] md:text-xs font-bold tracking-[0.2em] text-zinc-400 dark:text-zinc-500 uppercase mb-3">
              Auction Search
            </p>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight text-zinc-900 dark:text-zinc-100 mb-3">
              경매 물건을 찾고
              <br className="hidden md:block" />
              바로 검토를 시작하세요.
            </h1>
            <p className="text-sm md:text-base text-zinc-500 dark:text-zinc-400 leading-relaxed">
              지역, 물건 유형, 사건번호 기준으로 공개 사건을 찾을 수 있습니다.
              원하는 사건을 열면 검토 흐름으로 바로 이어집니다.
            </p>
          </div>
        </section>

        {/* Filter Bar & Auction List */}
        {loading ? (
          <div className="space-y-6">
            <FilterBar showSortOptions={false} totalCount={0} />
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-24 bg-zinc-200 dark:bg-zinc-800 rounded-lg"></div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="space-y-6">
            <FilterBar showSortOptions={false} />
            <div className="flex justify-center py-12">
              <div className="text-center">
                <div className="text-red-600 dark:text-red-400 mb-4">⚠️ {error}</div>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-zinc-900 dark:bg-zinc-700 text-white rounded-lg hover:bg-zinc-800 dark:hover:bg-zinc-600 transition"
                >
                  다시 불러오기
                </button>
              </div>
            </div>
          </div>
        ) : (
          <AuctionListWithFilters
            auctionsData={auctionsData}
            filters={{ regions, propertyTypes, sortOrder }}
            totalCount={totalCount}
            onSortChange={handleSortChange}
            onFiltersChange={(newFilters) => {
              if (process.env.NODE_ENV === 'development') {
                console.log('[AuctionListClient] 필터 변경됨, 데이터 리로딩 트리거:', newFilters);
              }
              setFiltersKey(prev => prev + 1);
            }}
          />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-12 flex justify-center gap-1 sm:gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Search className="w-3 h-3 sm:w-4 sm:h-4 rotate-90" />
            </button>

            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNumber = i + 1;
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={cn(
                    pageNumber > 3 ? 'hidden sm:flex' : 'flex',
                    'w-8 h-8 sm:w-10 sm:h-10 items-center justify-center rounded-lg font-bold text-xs sm:text-sm transition',
                    currentPage === pageNumber
                      ? 'bg-zinc-900 dark:bg-zinc-700 text-white shadow-md'
                      : 'border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400'
                  )}
                >
                  {pageNumber}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Search className="w-3 h-3 sm:w-4 sm:h-4 -rotate-90" />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
